import React from 'react';

import { Box, ButtonGroup, Divider, Flex, forwardRef, rem, StyleProps, useBreakpointValue } from '@bonusly/design-system';

// eslint-disable-next-line local-rules/no-cross-module-imports
import { useHomepageRedesignEnabled } from '../../../modules/bonus-feed/features/recognition/components/BonusFeed/hooks/useHomepageRedesignEnabled';
import { ActionModule } from '../types';
import { CollapsedButtons } from './decorations/collapsed-buttons';
import { useDecorationButtons } from './decorations/hooks/use-decoration-buttons';

// const MAIN_BUTTON_MODULES: ActionModule[] = ['emoji', 'gif', 'image', 'attachment'];
const COLLAPSED_BUTTON_MODULES: ActionModule[] = ['link', 'mention'];
const VIDEO_MODULE = 'video-recording';
const AUDIO_MODULE = 'audio-recording';
const MAX_VISIBLE_BUTTONS = 5;

const VerticalDivider = () => (
	<Box display="flex" alignItems="center" height={rem(40)}>
		<Divider orientation="vertical" height={rem(20)} mx={2} borderColor="gray.500" />
	</Box>
);

export const ActionDecorations = forwardRef<StyleProps, 'div'>((props: StyleProps, ref) => {
	const isMobile = useBreakpointValue({ base: true, sm: false });
	const buttons = useDecorationButtons();
	const isHomepageRedesignEnabled = useHomepageRedesignEnabled();

	if (!buttons.length) {
		return null;
	}
	const videoButton = buttons.find((button) => button.key === VIDEO_MODULE);
	const audioButton = buttons.find((button) => button.key === AUDIO_MODULE);

	//mobile buttons will only include link, mention, gif, and attachment
	const mobileButtons = buttons.filter((button) => button.key === 'link' || button.key === 'mention' || button.key === 'gif' || button.key === 'attachment');

	if (isMobile) {
		// mobileButtons should be in order of link, mention, gif, attachment
		const mobileButtonsOrdered = mobileButtons.sort((a, b) => {
			const order = ['link', 'mention', 'gif', 'attachment'];
			return order.indexOf(a.key as string) - order.indexOf(b.key as string);
		});

		// Redesigned mobile: collapsed buttons and video grouped together on the right
		if (isHomepageRedesignEnabled) {
			return (
				<ButtonGroup isAttached p={rem(1)} variant="ghost" ref={ref} {...props}>
					<CollapsedButtons buttons={mobileButtonsOrdered} />
					{videoButton}
					{audioButton}
				</ButtonGroup>
			);
		}

		// Original mobile: spread across full width
		return (
			<Flex justify="space-between" align="center" width="100%" ref={ref} {...props}>
				<ButtonGroup isAttached p={rem(1)} variant="ghost">
					<CollapsedButtons buttons={mobileButtonsOrdered} />
				</ButtonGroup>
				<Box display="flex" alignItems="center">
					<ButtonGroup isAttached p={rem(1)} variant="ghost" mr={rem(5)}>
						{videoButton}
						{audioButton}
					</ButtonGroup>
				</Box>
			</Flex>
		);
	}

	// Redesigned order: video, audio, gif, upload, emoji, then collapsed (link, mention)
	if (isHomepageRedesignEnabled) {
		const gifButton = buttons.find((button) => button.key === 'gif');
		const attachmentButton = buttons.find((button) => button.key === 'attachment');
		const imageButton = buttons.find((button) => button.key === 'image');
		const uploadButton = attachmentButton || imageButton;
		const emojiButton = buttons.find((button) => button.key === 'emoji');

		// Buttons to collapse: link and mention
		const collapsedRedesign = buttons.filter((button) => button.key === 'link' || button.key === 'mention');
		const collapsedButtonsList = collapsedRedesign.map((button) => React.cloneElement(button, { decorationVariant: 'list-item' }));

		return (
			<ButtonGroup isAttached p={rem(1)} ref={ref} variant="ghost" {...props}>
				{videoButton}
				{audioButton}
				{gifButton}
				{uploadButton}
				{emojiButton}
				{collapsedButtonsList.length > 0 && <CollapsedButtons buttons={collapsedButtonsList} />}
			</ButtonGroup>
		);
	}

	const collapsedButtons = buttons.filter((button) => COLLAPSED_BUTTON_MODULES.includes(button.key as ActionModule));

	// Lets collapse the buttons only if there are a lot of buttons and the video button is present
	const shouldCollapse = buttons.length > MAX_VISIBLE_BUTTONS && collapsedButtons.length == 2;

	if (!shouldCollapse) {
		return (
			<ButtonGroup isAttached p={rem(1)} ref={ref} variant="ghost" {...props}>
				{React.Children.toArray(buttons)}
			</ButtonGroup>
		);
	}
	const primaryNavigationButtons = buttons.filter((button) => button.key !== VIDEO_MODULE && !collapsedButtons.includes(button));

	// if buttons are collapsed make them list-item
	const collapsedButtonsList = collapsedButtons.map((button) => React.cloneElement(button, { decorationVariant: 'list-item' }));

	return (
		<ButtonGroup isAttached p={rem(1)} ref={ref} variant="ghost" {...props}>
			{React.Children.toArray(primaryNavigationButtons)}
			<CollapsedButtons buttons={collapsedButtonsList} />
			<VerticalDivider />
			{videoButton}
			{audioButton}
		</ButtonGroup>
	);
});

ActionDecorations.displayName = 'ActionDecorations';
